import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ClipboardList, Check, Undo2 } from 'lucide-react';
import { STATUS, STATUS_LABEL, STATUS_COLOUR, stageFor, logEntry } from '../noticeApproval';
import { fanOutRows } from '../noticeFanOut';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

// Activity notices: raised by OLE, approved by OLE, sent by OLE.
//
// A separate collection from parent_notice rather than a flag on it. The two are not the same
// letter with a different origin -- they are signed off by different people. A school that puts the
// ECA coordinator on activity notices and the office on general ones cannot express that with one
// shared approver list, and forcing it would mean one of those two groups signing things they have
// no business signing.
//
// What is NOT separate is the family's inbox. Approving here writes into parent_notice_recipient,
// the same rows a general notice produces, because a parent does not care which office a slip came
// from -- to them it is one thing to sign. One list, two chains that fill it.
const OleNotices = ({ activeSchoolId, studentMap, isMobile, canManage }) => {
  const { t } = useT();
  // Stored on the record; the stored word is the fallback.
  const statusWord = (v) => t(`parentNotice.status.${v}`, {}, STATUS_LABEL[v] || v);
  const [notices, setNotices] = useState([]);
  const [approval, setApproval] = useState({});
  const [busy, setBusy] = useState('');

  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const myStages = useMemo(() => stageFor({ approval }, { email: ownEmail }), [approval, ownEmail]);

  useEffect(() => {
    if (!activeSchoolId) return undefined;
    return onSnapshot(
      collection(db, 'schools', activeSchoolId, 'ole_notice'),
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ uid: d.id, ...d.data() }));
        rows.sort((a, b) => String(b.issue_datetime || '').localeCompare(String(a.issue_datetime || '')));
        setNotices(rows);
      },
      (e) => console.error('Error loading activity notices', e),
    );
  }, [activeSchoolId]);

  useEffect(() => {
    if (!activeSchoolId) return;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'ole_notice_settings'))
      .then((s) => setApproval((s.exists() ? s.data().approval : null) || {}))
      .catch((e) => console.error('Error loading activity notice approval settings', e));
  }, [activeSchoolId]);

  // Final approval is what puts the notice in front of families: the recipient rows are written
  // here, from the notice as it stands at the moment of approval. Doing it any earlier would mean an
  // unapproved letter was already readable.
  const decide = async (notice, action) => {
    const stage = notice.status === STATUS.PENDING_FINAL ? 'final' : 'first';
    let note = '';
    if (action === 'reject') {
      note = window.prompt(t('oleNotices.whyGoingBack')) || '';
      if (!note.trim()) { alert(t('oleNotices.reasonRequired')); return; }
    }

    setBusy(notice.uid);
    try {
      const next = action === 'reject'
        ? STATUS.REJECTED
        : (stage === 'first' ? STATUS.PENDING_FINAL : STATUS.APPROVED);

      if (next === STATUS.APPROVED) {
        await fanOutRows({
          db,
          schoolId: activeSchoolId,
          collectionName: 'parent_notice_recipient',
          keyPrefix: notice.noticeId,
          sids: notice.targetSids || [],
          studentBySid: studentMap,
          rowFor: () => ({
            noticeId: notice.noticeId,
            title: notice.title,
            content: notice.content,
            eventDates: notice.eventDates || [],
            activityId: notice.activityId || '',
            issuerId: notice.issuerId || '',
            issuerName: notice.issuerName || '',
            issue_datetime: notice.issue_datetime,
            attachmentUrl: '', attachmentName: '',
            signed: false,
          }),
        });
      }

      await setDoc(doc(db, 'schools', activeSchoolId, 'ole_notice', notice.uid), {
        status: next,
        approvalLog: [
          ...(notice.approvalLog || []),
          logEntry({ action, byEmail: ownEmail, stage, note }),
        ],
      }, { merge: true });
    } catch (e) {
      console.error('Approval failed', e);
      alert(t('oleNotices.recordFailed', { message: e.message }));
    }
    setBusy('');
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: isMobile ? 0 : 'var(--radius-md)',
    padding: isMobile ? '0.85rem' : '1rem',
  };

  if (notices.length === 0) {
    return (
      <div style={{ ...card, padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
        <ClipboardList size={32} style={{ opacity: 0.25, marginBottom: '0.75rem' }} />
        <div>{t('oleNotices.empty')}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {notices.map((n) => {
        const atFirst = n.status === STATUS.PENDING_FIRST;
        const atFinal = n.status === STATUS.PENDING_FINAL;
        // Being named for the final stage does not let somebody skip the first one -- skipping it
        // would mean the unit that owns the activity never saw it.
        const mine = (atFirst && myStages.first) || (atFinal && myStages.final) || canManage;

        return (
          <div key={n.uid} style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '0.95rem' }}>{n.title}</strong>
              <span style={{ fontSize: '0.78rem', color: STATUS_COLOUR[n.status], fontWeight: 700 }}>
                {statusWord(n.status)}
              </span>
              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                {t('oleNotices.recipients', { count: (n.targetSids || []).length })}
              </span>
            </div>

            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-subtle)', margin: '0.5rem 0 0', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {n.content}
            </p>

            {(n.approvalLog || []).length > 0 && (
              <div style={{ marginTop: '0.6rem', fontSize: '0.76rem', color: 'var(--color-text-muted)', lineHeight: 1.8 }}>
                {n.approvalLog.map((e, i) => (
                  <div key={i}>
                    <strong style={{ color: 'var(--color-text-main)' }}>{t(`oleNotices.actions.${e.action}`, {}, e.action)}</strong>
                    {e.stage ? ` (${t(`oleNotices.stageNames.${e.stage}`, {}, e.stage)})` : ''} — {e.byName || e.byEmail}
                    {e.note ? ` — “${e.note}”` : ''}
                  </div>
                ))}
              </div>
            )}

            {(atFirst || atFinal) && mine && (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', flex: 1 }}>
                  {atFinal ? t('oleNotices.approveSendsToFamilies') : t('oleNotices.approvePassesOn')}
                </span>
                <button className="btn-outline" disabled={busy === n.uid} onClick={() => decide(n, 'reject')}
                        style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Undo2 size={14} /> {t('oleNotices.sendBack')}
                </button>
                <button className="btn-primary" disabled={busy === n.uid} onClick={() => decide(n, 'approve')}
                        style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Check size={14} /> {t('oleNotices.approve')}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OleNotices;
